/**
 * WHAT:
 *   Component for displaying extracted raw PDF text.
 *
 * WHY:
 *   Centralizes raw PDF text display UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - TypeScript types (RawPdfTextDisplayProps)
 *
 * RISKS:
 *   - NOTE: Large PDF text may cause performance issues during rendering
 *   - NOTE: Text overflow handling may not work correctly in all browsers
 */

import type { RawPdfTextDisplayProperties } from "../types";

import { type FC } from "react";

/**
 * WHAT:
 *   Component displaying extracted raw PDF text with statistics.
 *
 * WHY:
 *   Encapsulates raw PDF text display UI logic.
 *
 * @param root0
 *
 * @param root0.rawPdfText
 *
 * @returns {JSX.Element | null} The RawPdfTextDisplay component or null if no text
 *
 */
export const RawPdfTextDisplay: FC<RawPdfTextDisplayProperties> = ({ rawPdfText }) => {
  if (!rawPdfText) {
    return null;
  }

  return (
    <div className="mt-6 p-4 bg-slate-600/30 rounded-lg">
      <h3 className="text-lg font-semibold text-white mb-4">Extracted PDF Text</h3>
      <div className="bg-slate-800/50 rounded-lg p-4 max-h-96 overflow-y-auto">
        <pre className="text-slate-300 text-sm whitespace-pre-wrap font-mono">{rawPdfText}</pre>
      </div>
      <div className="mt-2 text-xs text-slate-400">
        Characters: {String(rawPdfText.length)} | Lines: {String(rawPdfText.split("\n").length)}
      </div>
    </div>
  );
};
